/* F23 · Las piezas con su maqueta.

   Un entregable de AI trae, además del texto, una lista de piezas con su
   categoría: social, OOH, web, email, radio, TV, print, punto de venta,
   documento, calendario. Aquí cada categoría se pinta dentro del marco de su
   formato —el teléfono, la valla, el navegador, el sobre, el guion— para que
   quien firma vea QUÉ es y DÓNDE va, no un bloque de texto que hay que imaginar.

   Son marcos de CSS, no piezas finales: dicen "esto es un post y se ve así",
   no "esto es la foto". El texto completo sigue debajo, plegado, porque la
   maqueta es una forma de leerlo y no un sustituto. */
import type { CategoriaPieza, Pieza } from "../api";
import { esc } from "../ui";

const CAT: Record<CategoriaPieza, { nombre: string; donde: string }> = {
  social:     { nombre: "Social",          donde: "feed o historia de la plataforma" },
  ooh:        { nombre: "OOH · vía pública", donde: "valla, parada, mupi o digital exterior" },
  web:        { nombre: "Web",             donde: "sección de la landing o del sitio" },
  email:      { nombre: "Email",           donde: "bandeja de entrada del cliente" },
  radio:      { nombre: "Radio",           donde: "cuña al aire, con su duración" },
  tv:         { nombre: "TV",              donde: "spot en pantalla, con su duración" },
  print:      { nombre: "Print",           donde: "página de revista, prensa o folleto" },
  pos:        { nombre: "Punto de venta",  donde: "góndola, mostrador, exhibidor" },
  documento:  { nombre: "Documento",       donde: "para leer: análisis, síntesis, propuesta" },
  calendario: { nombre: "Calendario",      donde: "qué sale qué día y en qué plataforma" },
};
const ORDEN: CategoriaPieza[] = ["social", "ooh", "web", "email", "tv", "radio", "print", "pos", "calendario", "documento"];

const t = (v: unknown) => (v == null ? "" : Array.isArray(v) ? v.map(String).join(" ") : String(v));
const lista = (v: unknown): string[] => Array.isArray(v) ? v.map(String) : t(v) ? t(v).split(/[\s,]+/).filter(Boolean) : [];

/* El guion: líneas "VOZ: texto". Lo que no lleva voz se cuelga de la anterior. */
function guion(v: unknown) {
  const lineas = t(v).split("\n").map((l) => l.trim()).filter(Boolean);
  const out: { voz: string; texto: string }[] = [];
  for (const l of lineas) {
    const m = l.match(/^([A-ZÁÉÍÓÚÑ][A-ZÁÉÍÓÚÑ0-9 .]{1,18}):\s*(.+)$/);
    if (m) out.push({ voz: m[1]!, texto: m[2]! });
    else if (out.length) out[out.length - 1]!.texto += " " + l;
    else out.push({ voz: "", texto: l });
  }
  return out;
}

function marco(p: Pieza): string {
  const c = p.campos;
  switch (p.categoria) {
    case "social": {
      const historia = /histor|story|reel/i.test(p.formato);
      return '<div class="mk-tel' + (historia ? " historia" : "") + '"><div class="barra-tel"></div>' +
        '<div class="cuenta"><span class="av">' + esc(t(c.plataforma).slice(0, 2).toUpperCase() || "IG") + "</span>" + esc(t(c.plataforma) || "Social") + "</div>" +
        '<div class="visual">' + (historia ? esc(t(c.copy)) : esc(t(c.visual) || "visual por definir")) + "</div>" +
        (historia ? "" : '<div class="acciones-tel">♡ ◯ ⇪</div><div class="texto">' + esc(t(c.copy)) +
          (lista(c.hashtags).length ? '<div class="tags">' + lista(c.hashtags).map((h) => esc(h.startsWith("#") ? h : "#" + h)).join(" ") + "</div>" : "") +
          (t(c.cta) ? '<div class="cta">' + esc(t(c.cta)) + "</div>" : "") + "</div>") +
        (historia && t(c.cta) ? '<div class="texto" style="text-align:center"><div class="cta">' + esc(t(c.cta)) + "</div></div>" : "") +
        "</div>";
    }
    case "ooh":
      return '<div class="mk-valla"><div class="cara"><div class="logo-p"></div><div class="h">' + esc(t(c.headline)) + "</div>" +
        (t(c.subhead) ? '<div class="s">' + esc(t(c.subhead)) + "</div>" : "") + (t(c.cta) ? '<div class="cta">' + esc(t(c.cta)) + "</div>" : "") +
        (t(c.legal) ? '<div class="legal">' + esc(t(c.legal)) + "</div>" : "") + '</div><div class="postes"></div></div>';
    case "web":
      return '<div class="mk-web"><div class="chrome"><i></i><i></i><i></i><span class="url">' + esc(t(c.url) || "cliente.com/" + t(c.seccion).toLowerCase().replace(/\s+/g, "-")) + '</span></div>' +
        '<div class="hero">' + (t(c.seccion) ? '<div class="seccion">' + esc(t(c.seccion)) + "</div>" : "") + '<div class="h">' + esc(t(c.headline)) + "</div>" +
        (t(c.cuerpo) ? '<div class="cuerpo">' + esc(t(c.cuerpo)) + "</div>" : "") + (t(c.cta) ? '<span class="boton">' + esc(t(c.cta)) + "</span>" : "") + "</div></div>";
    case "email":
      return '<div class="mk-mail"><div class="cabecera-mail"><div class="asunto">' + esc(t(c.asunto) || p.titulo) + "</div>" + (t(c.preheader) ? '<div class="pre">' + esc(t(c.preheader)) + "</div>" : "") + "</div>" +
        '<div class="cuerpo-mail">' + esc(t(c.cuerpo)) + (t(c.cta) ? '<br><span class="boton">' + esc(t(c.cta)) + "</span>" : "") + "</div></div>";
    case "radio": case "tv":
      return '<div class="mk-guion"><div class="cab-g"><span class="reloj">' + esc(t(c.duracion) || "—") + "</span>" + esc(p.formato) + "</div>" +
        guion(c.guion).map((l) => '<div class="linea"><div class="voz">' + esc(l.voz) + '</div><div>' + esc(l.texto) + "</div></div>").join("") +
        (t(c.visual) || t(c.sonido) ? '<div class="visual-g">' + esc(t(c.visual) || t(c.sonido)) + "</div>" : "") + "</div>";
    case "print": case "pos":
      return '<div class="mk-pag' + (p.categoria === "pos" ? " pos" : "") + '"><div class="h">' + esc(t(c.headline)) + "</div>" +
        (t(c.cuerpo) ? '<div class="cuerpo">' + esc(t(c.cuerpo)) + "</div>" : "") + (t(c.cta) ? '<div class="cta">' + esc(t(c.cta)) + "</div>" : "") +
        (t(c.legal) ? '<div class="legal">' + esc(t(c.legal)) + "</div>" : "") + "</div>";
    case "calendario": {
      const dias = Array.isArray(c.dias) ? (c.dias as Record<string, unknown>[]) : [];
      return '<div class="mk-cal">' + (dias.length ? dias.map((d) => '<div class="d"><span class="dia">' + esc(t(d.dia)) + '</span><span class="plat">' + esc(t(d.plataforma)) + '</span><span class="copy">' + esc(t(d.copy)) + "</span></div>").join("") : '<div class="d"><span class="copy">Sin días.</span></div>') + "</div>";
    }
    case "documento": default: {
      const secs = Array.isArray(c.secciones) ? (c.secciones as Record<string, unknown>[]) : [];
      return '<div class="mk-doc">' + (secs.length
        ? secs.map((x) => '<div class="sec"><div class="st">' + esc(t(x.titulo)) + '</div><div class="sx">' + esc(t(x.texto)) + "</div></div>").join("")
        : '<div class="sec"><div class="sx">' + esc(Object.values(c).map(t).join("\n")) + "</div></div>") + "</div>";
    }
  }
}

/** Las piezas agrupadas por categoría, cada una dentro de su marco. */
export function piezasHtml(piezas: Pieza[] | undefined): string {
  if (!piezas?.length) return "";
  const grupos = ORDEN.filter((k) => piezas.some((p) => p.categoria === k));
  return '<div class="piezas">' + grupos.map((k) => {
    const ps = piezas.filter((p) => p.categoria === k);
    return '<div class="cat">' + esc(CAT[k].nombre) + '<span class="n">' + ps.length + "</span><span style=\"font-weight:500;letter-spacing:0;text-transform:none\">· " + esc(CAT[k].donde) + "</span></div>" +
      '<div class="fila-piezas">' + ps.map((p) => '<div class="pieza"><div class="rotulo"><b>' + esc(p.titulo) + "</b>" + (p.formato && p.formato !== p.titulo ? "· " + esc(p.formato) : "") + "</div>" + marco(p) + "</div>").join("") + "</div>";
  }).join("") + "</div>";
}

/** Un resumen de una línea: "3 social · 1 calendario". */
export function resumenPiezas(piezas: Pieza[] | undefined): string {
  if (!piezas?.length) return "";
  return ORDEN.filter((k) => piezas.some((p) => p.categoria === k)).map((k) => piezas.filter((p) => p.categoria === k).length + " " + CAT[k].nombre.toLowerCase()).join(" · ");
}
